/**
 * Test utilities for NullEngine-based testing
 * Provides fast, reliable testing without WebGL dependencies
 */

import {
  Color4,
  HemisphericLight,
  NullEngine,
  Scene,
  type Scene as SceneType,
  UniversalCamera,
  Vector3,
} from "@babylonjs/core";
import type { EngineManager } from "../managers/engine/EngineManager";
import type { EngineOptions } from "../managers/engine/types";
import type { SceneManager } from "../managers/scene/SceneManager";
import type { SceneOptions } from "../managers/scene/types";

/**
 * Options for creating a test engine
 */
export interface TestEngineOptions {
  /**
   * Engine width
   */
  width?: number;

  /**
   * Engine height
   */
  height?: number;

  /**
   * Enable stencil buffer
   */
  stencil?: boolean;

  /**
   * Enable antialias
   */
  antialias?: boolean;
}

/**
 * Options for creating a test scene
 */
export interface TestSceneOptions extends SceneOptions {
  /**
   * Create a default camera
   */
  withCamera?: boolean;

  /**
   * Create a default light
   */
  withLight?: boolean;

  /**
   * Camera configuration
   */
  cameraConfig?: {
    name?: string;
    position?: { x: number; y: number; z: number };
  };

  /**
   * Light configuration
   */
  lightConfig?: {
    name?: string;
    intensity?: number;
  };
}

/**
 * Create a NullEngine for testing
 * Provides fast, deterministic testing without WebGL dependencies
 * 10-100x faster than WebGL-based tests
 * @param options Engine options
 * @returns NullEngine instance ready for testing
 * @example
 * ```typescript
 * const engine = createTestEngine({ width: 1024, height: 768 });
 * const scene = createTestScene(engine);
 * ```
 */
export function createTestEngine(options: TestEngineOptions = {}): NullEngine {
  const engine = new NullEngine({
    deterministicLockstep: true,
    lockstepMaxSteps: 1,
    renderHeight: options.height ?? 256,
    renderWidth: options.width ?? 512,
    textureSize: 512,
  });

  // Set additional options
  if (options.stencil !== undefined) {
    engine.setStencilBuffer(options.stencil);
  }

  return engine;
}

/**
 * Create a test scene with NullEngine
 * @param engine The engine to use
 * @param options Scene options
 */
export function createTestScene(
  engine: NullEngine,
  options: TestSceneOptions = {},
): SceneType {
  const scene = new Scene(engine);

  // Apply scene options
  if (options.clearColor) {
    scene.clearColor = new Color4(
      options.clearColor.r,
      options.clearColor.g,
      options.clearColor.b,
      options.clearColor.a ?? 1,
    );
  }

  // Add default camera if requested
  if (options.withCamera) {
    const cameraName = options.cameraConfig?.name ?? "testCamera";
    const position = options.cameraConfig?.position ?? {
      x: 0,
      y: 0,
      z: -10,
    };
    const camera = new UniversalCamera(
      cameraName,
      new Vector3(position.x, position.y, position.z),
      scene,
    );
    camera.setTarget(Vector3.Zero());
    scene.activeCamera = camera;
  }

  // Add default light if requested
  if (options.withLight) {
    const lightName = options.lightConfig?.name ?? "testLight";
    const light = new HemisphericLight(lightName, new Vector3(0, 1, 0), scene);
    if (options.lightConfig?.intensity !== undefined) {
      light.intensity = options.lightConfig.intensity;
    }
  }

  return scene;
}

/**
 * Create engine options for testing
 * @param overrides Option overrides
 */
export function createTestEngineOptions(
  overrides: Partial<EngineOptions> = {},
): EngineOptions {
  return {
    type: "null",
    ...overrides,
  };
}

/**
 * Create scene options for testing
 * @param overrides Option overrides
 */
export function createTestSceneOptions(
  overrides: Partial<SceneOptions> = {},
): SceneOptions {
  return {
    clearColor: { a: 1, b: 0.3, g: 0.2, r: 0.2 },
    ...overrides,
  };
}

/**
 * Create a test environment with engine and scene
 */
export async function createTestEnvironment(
  options: {
    engineOptions?: TestEngineOptions;
    sceneOptions?: TestSceneOptions;
  } = {},
): Promise<{ engine: NullEngine; scene: SceneType; cleanup: () => void }> {
  const engine = createTestEngine(options.engineOptions);
  const scene = createTestScene(engine, options.sceneOptions);

  const cleanup = () => {
    scene.dispose();
    engine.dispose();
  };

  return { cleanup, engine, scene };
}

/**
 * Run a test with automatic cleanup
 */
export async function runWithTestEnvironment<T>(
  test: (env: { engine: NullEngine; scene: SceneType }) => Promise<T> | T,
  options: {
    engineOptions?: TestEngineOptions;
    sceneOptions?: TestSceneOptions;
  } = {},
): Promise<T> {
  const { engine, scene, cleanup } = await createTestEnvironment(options);
  try {
    return await test({ engine, scene });
  } finally {
    cleanup();
  }
}

/**
 * Wait for scene to be ready
 */
export function waitForSceneReady(scene: SceneType): Promise<void> {
  return new Promise((resolve) => {
    if (scene.isReady()) {
      resolve();
    } else {
      scene.executeWhenReady(() => resolve());
    }
  });
}

/**
 * Advance the render loop for testing
 */
export function advanceRenderLoop(scene: SceneType, frames = 1): void {
  for (let i = 0; i < frames; i++) {
    scene.render();
  }
}

/**
 * Create a mock EngineManager for testing
 */
export function createMockEngineManager(): Partial<EngineManager> {
  const engine = createTestEngine();
  return {
    createEngine: async () => engine,
    dispose: () => {
      engine.dispose();
    },
    getEngine: () => engine,
    isInitialized: true,
  };
}

/**
 * Create a mock SceneManager for testing
 */
export function createMockSceneManager(): Partial<SceneManager> {
  let scene: SceneType | null = null;
  return {
    clearScene: () => {
      scene?.dispose();
      scene = null;
    },
    createScene: async (engine: unknown) => {
      scene = createTestScene(engine as NullEngine);
      return Promise.resolve(scene);
    },
    dispose: () => {
      scene?.dispose();
    },
    getScene: () => scene,
    isInitialized: true,
  };
}
